import { create } from 'zustand';
import type { Prayer } from '@/types/prayer';
import type { PrayerCorpusHydrationV1 } from '@/lib/legacy-grouping-notice';
import * as db from '@/lib/db';
import {
  queuePrayerMutation,
  queuePrayerMutationCas,
  requestContentLifecycleSync,
} from '@/lib/sync';
import { useSettingsStore } from '@/lib/settings-store';
import { chainKeyedWrite } from '@/lib/write-chain';
import { assertCoreWriteAllowed, isCoreReadOnly } from '@/lib/core-cutover';
import { getContentOwnerBinding } from '@/lib/content-owner';
import {
  getOwnerContentEntities,
  getLocalContentEntity,
  hasContentLifecycleEntityBootstrap,
  type ContentLifecycleAuthExpectation,
} from '@/lib/content-lifecycle/store';
import { completePrayerDocument, prayerFromCompleteDocument } from '@/lib/content-lifecycle/contracts';
import { getAuthEpoch, getAuthUserId, waitForAuthResolution } from '@/lib/keepsake/epochs';
import type { CompletePrayerDocument } from '@/types/content-lifecycle';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';
import { captureQueueListFence, useAudioQueueStore } from '@/lib/audio-queue-store';
import {
  pendingTokensEqual,
} from '@/lib/progress-entry-identity';
import {
  ContentHydrationStaleError,
  recordContentHydrationJoinedLoad,
  runContentHydrationPublishProbe,
} from '@/lib/content-hydration';

// Core-content cutover, layer 2 of the write guard (db.ts is layer 1).
// Every mutation action rejects here FIRST so UI callers can catch a
// CoreReadOnlyError and render the maintenance state instead of failing
// deeper in the persistence layer. Read actions (loadPrayers) are never
// guarded.

// The narrowed patch surface for `patchPrayer` (share-scripture-found
// arc, plan section 3): fields are added to this Pick deliberately, per
// new caller. Identity, timestamps, sync bookkeeping (syncedAt), and the
// audio-queue fields (pendingTranscriptionId / transcriptionFailed) are
// structurally outside it so a fire-and-forget patch can never corrupt
// the outbox or the V.B recovery lane.
export type PrayerPatch = Partial<Pick<Prayer, 'verseResult'>>;

// Per-prayer write serialization for patchPrayer. The verse lane persists
// verseResult fire-and-forget (share-flow save + drift self-heal), which can
// land within milliseconds of an unrelated updatePrayer write; unserialized
// read-then-write calls would let one full-object write clobber the other's
// just-landed field. See src/lib/write-chain.ts for the compare-and-delete
// cleanup invariant.
const prayerWriteChains = new Map<string, Promise<void>>();
let prayerLoadRequestId = 0;
let prayerLoadInFlight: { key: string; promise: Promise<void> } | null = null;
let prayerMutationVersion = 0;
const prayerMutationVersions = new Map<string, number>();

function markPrayerMutation(id: string): void {
  prayerMutationVersion += 1;
  prayerMutationVersions.set(id, prayerMutationVersion);
}

function mergePrayerMutations(
  loaded: Prayer[],
  current: Prayer[],
  startedAtMutationVersion: number,
): Prayer[] {
  const changed = new Set(
    [...prayerMutationVersions]
      .filter(([, version]) => version > startedAtMutationVersion)
      .map(([id]) => id),
  );
  if (changed.size === 0) return loaded;
  return [
    ...loaded.filter((prayer) => !changed.has(prayer.id)),
    ...current.filter((prayer) => changed.has(prayer.id)),
  ].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/**
 * Plain-data structural equality for Prayer content (garden-remember readiness
 * P3, 2026-10-16). Keys whose value is undefined count as absent (JSON
 * semantics, the lifecycle document shape). Anything that is not plain data
 * (a class instance, Date, Map, Blob, function) is never treated as equal, so
 * unusual content can only cost a cache miss, never a wrong reuse.
 */
function plainDataEqual(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (typeof left !== typeof right || left === null || right === null) return false;
  if (typeof left !== 'object') return Number.isNaN(left) && Number.isNaN(right);
  if (Array.isArray(left) !== Array.isArray(right)) return false;
  if (Array.isArray(left)) {
    const other = right as unknown[];
    if (left.length !== other.length) return false;
    for (let index = 0; index < left.length; index += 1) {
      if (!plainDataEqual(left[index], other[index])) return false;
    }
    return true;
  }
  const plain = (value: object) => {
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
  };
  if (!plain(left) || !plain(right as object)) return false;
  const a = left as Record<string, unknown>;
  const b = right as Record<string, unknown>;
  const keys = (record: Record<string, unknown>) => Object.keys(record).filter((key) => record[key] !== undefined);
  const aKeys = keys(a);
  if (aKeys.length !== keys(b).length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key) || !plainDataEqual(a[key], b[key])) return false;
  }
  return true;
}

/**
 * Keep object identity for unchanged prayers when a load publishes (P3). An
 * entry whose complete content equals the previously published Prayer with
 * the same id reuses that object; when every entry is reused in the same
 * order, the previous array itself is kept. Only called for a load published
 * under the same owner, auth epoch and read-only mode as the previous one.
 * Consumers keyed on identity (search caches, derived Remember moments,
 * memoized lists) therefore do no work for a sync that changed nothing.
 */
export function reuseUnchangedPrayers(next: Prayer[], previous: Prayer[]): Prayer[] {
  if (previous.length === 0 || next.length === 0) return next;
  const previousById = new Map(previous.map((prayer) => [prayer.id, prayer]));
  let allReused = next.length === previous.length;
  const result = next.map((prayer, index) => {
    const prior = previousById.get(prayer.id);
    const reused = prior !== undefined && (prior === prayer || plainDataEqual(prior, prayer)) ? prior : prayer;
    if (reused !== previous[index]) allReused = false;
    return reused;
  });
  return allReused ? previous : result;
}

/** Owner/auth-epoch/mode of the last published load; reuse only within it. */
let publishedPrayerScope: string | null = null;

interface AdiaState {
  prayers: Prayer[];
  isLoading: boolean;
  /**
   * PrayerCorpusHydrationV1 (CR, PC-B rev 9 decision 6). `generation`
   * advances ONLY when one complete load installs, so the boot
   * false-empty window and a failed load can never masquerade as a
   * ready empty corpus. The legacy-grouping notice defers unless this
   * is `ready`.
   */
  hydration: PrayerCorpusHydrationV1;

  loadPrayers: () => Promise<void>;
  publishPrayer: (prayer: Prayer, expectedAuth: ContentLifecycleAuthExpectation) => void;
  addPrayer: (
    prayer: Prayer,
    recoveredAudio?: readonly RecoveredAudioCommitClaim[],
  ) => Promise<void>;
  updatePrayer: (
    prayer: Prayer,
    expectedAuth?: ContentLifecycleAuthExpectation,
  ) => Promise<void>;
  /**
   * Recovery-only variant: the Prayer mutation and exact queue-row
   * consumption share one owner-checked content-lifecycle transaction.
   */
  updatePrayerWithRecoveredAudio: (
    prayer: Prayer,
    recoveredAudio: readonly RecoveredAudioCommitClaim[],
    expectedAuth?: ContentLifecycleAuthExpectation,
  ) => Promise<RecoveredAudioCommitClaim[]>;
  /**
   * Serialized derived-metadata update against the authoritative row. Verse
   * lookup/self-heal is not a new authored event: preserve its recorded times
   * and delegate through the existing guarded enrichment transaction. No-ops when the prayer is gone (deleted mid-flight) or
   * when `guard(latest)` returns false — the guard runs inside the
   * serialized closure, which is what closes the read-then-write race a
   * caller-side check would reopen.
   */
  patchPrayer: (
    id: string,
    patch: PrayerPatch,
    guard?: (latest: Prayer) => boolean,
  ) => Promise<void>;
  /**
   * Atomic guarded enrichment write (offline-first Chunk 2). Delegates to
   * db.guardedPatchPrayer so guard + put run in ONE IDB transaction — atomic
   * against every IDB writer including the sync pull. Returns false when the
   * guard fails (nothing written). On success swaps the row into the store
   * with a staleness-guarded set (never clobbers a later local edit, never
   * re-adds a deleted prayer) and runs pushAndMark (the enrichment changed
   * synced fields). See plan §3.1a / §3.2.
   */
  applyEnrichment: (
    id: string,
    guard: (row: Prayer) => boolean,
    computeNext: (row: Prayer) => Prayer,
  ) => Promise<boolean>;
  /**
   * Local-only clear of a superseded enrichment token (the deleted-target
   * case, plan §3.1a). Clears pendingEnrichment WITHOUT bumping updatedAt and
   * WITHOUT pushing (a local-only field never pushes a cloud row).
   */
  clearPendingEnrichment: (
    id: string,
    target: Prayer['pendingEnrichment'],
  ) => Promise<void>;
  removePrayer: (id: string) => Promise<void>;
}

export const useAdiaStore = create<AdiaState>((set, get) => ({
  prayers: [],
  isLoading: false,
  hydration: { state: 'not-started', generation: 0 },

  publishPrayer: (prayer, expectedAuth) => {
    if (getAuthUserId() !== expectedAuth.ownerId || getAuthEpoch() !== expectedAuth.authEpoch) return;
    markPrayerMutation(prayer.id);
    set(state => ({ prayers: [prayer, ...state.prayers.filter(item => item.id !== prayer.id)]
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)) }));
  },

  loadPrayers: async () => {
    await waitForAuthResolution();
    const readOnly = isCoreReadOnly();
    const ownerId = getAuthUserId();
    const authEpoch = getAuthEpoch();
    const loadKey = `${ownerId ?? 'signed-out'}\n${authEpoch}\n${readOnly ? 'read-only' : 'writable'}`;
    if (prayerLoadInFlight?.key === loadKey) {
      recordContentHydrationJoinedLoad('prayers');
      return prayerLoadInFlight.promise;
    }
    const requestId = ++prayerLoadRequestId;
    const startedAtMutationVersion = prayerMutationVersion;
    set((s) => ({
      isLoading: true,
      hydration: { state: 'loading', generation: s.hydration.generation },
    }));
    const promise = (async () => {
      try {
      const binding = ownerId ? await getContentOwnerBinding() : null;
      const authorizedOwner = Boolean(ownerId && binding === ownerId);
      let prayers: Prayer[];
      if (!authorizedOwner) {
        prayers = [];
      } else if (readOnly) {
        prayers = await db.getAllPrayers();
      } else {
        const [entities, legacy, initialized] = await Promise.all([
          getOwnerContentEntities(ownerId as string),
          db.getAllPrayers(),
          hasContentLifecycleEntityBootstrap(ownerId as string, 'prayer'),
        ]);
        const legacyById = new Map(legacy.map((prayer) => [prayer.id, prayer]));
        const lifecyclePrayers = entities
          .filter((entity) => entity.entityType === 'prayer' && entity.visibleDocument !== null);
        // LC6 (operator decision, 2026-10-19): a Prayer whose recording is still
        // pending is never a lifecycle entity (the pending-transcription sync
        // gate keeps it local until its words land), so the lifecycle list alone
        // dropped it, and its recovery surface, on every later load. It is
        // listed from the stored rows of this same authorized read. Any entity
        // row for its id, live or tombstoned, wins. Listing authors nothing.
        const prayerEntityIds = new Set(
          entities.filter((entity) => entity.entityType === 'prayer').map((entity) => entity.entityId),
        );
        prayers = !initialized && lifecyclePrayers.length === 0
          ? legacy
          : [
            ...lifecyclePrayers
              .map((entity) =>
                prayerFromCompleteDocument(
                  entity.visibleDocument as CompletePrayerDocument,
                  legacyById.get(entity.entityId),
                ),
              ),
            ...legacy.filter((prayer) => Boolean(prayer.pendingTranscriptionId) && !prayerEntityIds.has(prayer.id)),
          ];
        prayers.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      }
      await runContentHydrationPublishProbe('prayers');
      // One COMPLETE load installs: ready + advanced generation, in the
      // same set as the corpus swap so no reader can observe ready with
      // a stale corpus.
      const finalBinding = ownerId ? await getContentOwnerBinding() : null;
      const finalAuthorizedOwner = Boolean(ownerId && finalBinding === ownerId);
      const canPublish =
        requestId === prayerLoadRequestId &&
        get().hydration.state === 'loading' &&
        isCoreReadOnly() === readOnly &&
        getAuthUserId() === ownerId &&
        getAuthEpoch() === authEpoch &&
        finalAuthorizedOwner === authorizedOwner;
      if (canPublish) {
        set((s) => {
          const merged = authorizedOwner
            ? mergePrayerMutations(prayers, get().prayers, startedAtMutationVersion)
            : [];
          // P3: unchanged prayers keep their objects (and an unchanged list
          // keeps its array) only within the same owner/epoch/mode scope.
          // Readiness and the generation still advance on every complete load.
          const published = publishedPrayerScope === loadKey
            ? reuseUnchangedPrayers(merged, s.prayers)
            : merged;
          publishedPrayerScope = loadKey;
          return {
            prayers: published,
            hydration: { state: 'ready', generation: s.hydration.generation + 1 },
          };
        });
      } else {
        throw new ContentHydrationStaleError('owner-auth-or-request-changed');
      }
    } catch (err) {
      // Load failure is `failed`, never a ready empty corpus. Rethrow so
      // awaiting callers (sync.ts) keep their existing failure semantics.
      if (requestId === prayerLoadRequestId && !(err instanceof ContentHydrationStaleError)) {
        set((s) => ({
          hydration: { state: 'failed', generation: s.hydration.generation },
        }));
      } else if (requestId === prayerLoadRequestId) {
        set((s) => ({
          hydration: { state: 'not-started', generation: s.hydration.generation },
        }));
      }
      throw err;
      } finally {
        if (requestId === prayerLoadRequestId) set({ isLoading: false });
      }
    })();
    prayerLoadInFlight = { key: loadKey, promise };
    try {
      await promise;
    } finally {
      if (prayerLoadInFlight?.promise === promise) prayerLoadInFlight = null;
    }
  },

  addPrayer: async (prayer, recoveredAudio = []) => {
    assertCoreWriteAllowed('store:prayer:add');
    const ownerId = getAuthUserId();
    const authEpoch = getAuthEpoch();
    const audioListFence = captureQueueListFence();
    if (prayer.pendingTranscriptionId) {
      if (recoveredAudio.length > 0) {
        throw new Error('pending-prayer-inline-audio-refused');
      }
      // Audio recovery stubs are deliberately local-only until transcription
      // completes; they are the sole exception to ordinary lifecycle authorship.
      await db.savePrayer(prayer);
    } else {
      if (!await queuePrayerMutation(prayer, recoveredAudio)) throw new Error('prayer-create-refused');
    }
    try {
      if (prayer.pendingTranscriptionId && ownerId === getAuthUserId() && authEpoch === getAuthEpoch()) {
        markPrayerMutation(prayer.id);
        set({ prayers: [prayer, ...get().prayers.filter(item => item.id !== prayer.id)] });
      }
    } catch { /* hydrate repairs observers after successful local commit */ }
    if (!prayer.pendingTranscriptionId) requestContentLifecycleSync();
    // Mirror refresh is a deferred effect after the durable local commit.
    void Promise.all(recoveredAudio.map(async (claim) => {
      const row = await db.getAudioQueueRow(claim.queueItemId);
      if (row) useAudioQueueStore.getState().updateItem(audioListFence, row.id, row);
    })).catch(() => {});
  },

  updatePrayer: async (prayer, expectedAuth) => {
    await get().updatePrayerWithRecoveredAudio(prayer, [], expectedAuth);
  },

  updatePrayerWithRecoveredAudio: async () => {
    // Closed compatibility surface: all authored writes require explicit intent.
    throw new Error('prayer-command-required');
  },

  // Async so the read-only rejection surfaces as a promise rejection
  // (both call sites already `.catch`), never as a synchronous throw out
  // of a fire-and-forget `void patchPrayer(...)` expression.
  patchPrayer: async (id, patch, guard) => {
    assertCoreWriteAllowed('store:prayer:patch');
    return chainKeyedWrite(prayerWriteChains, id, async () => {
      await get().applyEnrichment(id, (latest) => !guard || guard(latest),
        (latest) => ({ ...latest, ...patch, createdAt: latest.createdAt, updatedAt: latest.updatedAt }));
    });
  },

  applyEnrichment: async (id, guard, computeNext) => {
    assertCoreWriteAllowed('store:prayer:apply-enrichment');
    const auth = { ownerId: getAuthUserId() ?? '', authEpoch: getAuthEpoch() };
    const currentAuth = () => getAuthUserId() === auth.ownerId && getAuthEpoch() === auth.authEpoch;
    const next = await queuePrayerMutationCas(id, row => currentAuth() && guard(row), computeNext);
    if (!next) return false;
    void getLocalContentEntity(auth.ownerId, 'prayer', id).then(current => {
      if (current?.visibleDocument && JSON.stringify(current.visibleDocument) === JSON.stringify(completePrayerDocument(next))) get().publishPrayer(next, auth);
    }).catch(() => {});
    requestContentLifecycleSync();
    return true;
  },

  clearPendingEnrichment: async (id, target) => {
    assertCoreWriteAllowed('store:prayer:clear-pending-enrichment');
    const auth = { ownerId: getAuthUserId() ?? '', authEpoch: getAuthEpoch() };
    const next = await db.guardedPatchPrayer(
      id,
      (r) => pendingTokensEqual(r.pendingEnrichment, target),
      (r) => ({ ...r, pendingEnrichment: null }),
    );
    if (next === null || getAuthUserId() !== auth.ownerId || getAuthEpoch() !== auth.authEpoch) return;
    markPrayerMutation(id);
    set((s) => ({
      prayers: s.prayers.map((p) =>
        p.id === next.id && pendingTokensEqual(p.pendingEnrichment, target)
          ? { ...p, pendingEnrichment: null }
          : p,
      ),
    }));
    // No pushAndMark: clearing a local-only field never pushes a cloud row.
  },

  removePrayer: async (id) => {
    // This action is LOCAL-ONLY and is restricted to the
    // never-synced recovery lane (rule #24's discardFailedAudio, whose
    // prayers were never pushed per the rule #42 gate, so there is no
    // server row to orphan). User-facing delete surfaces route through
    // deletePrayerProtected (keepsake/prayer-actions.ts): durable intent
    // journal + the owner-checked validity RPC's soft delete.
    assertCoreWriteAllowed('store:prayer:remove');
    await db.deletePrayer(id);
    markPrayerMutation(id);
    set({ prayers: get().prayers.filter((p) => p.id !== id) });
    // Clear the LeafButton's first-visit marker so an undo + reopen
    // re-triggers the breathing glow on this prayer.
    useSettingsStore.getState().forgetLeafGlowSeen(id);
  },
}));
